import { useEffect, useRef, useState } from "react";
import { loadActiveCampaigns, pickCampaignForEvent } from "@/lib/adEngine";
import type { AdCampaign, AdTriggerEvent } from "@/types";

interface Frame {
  inDanger?: boolean;
  isGoal?: boolean;
  minute?: number;
  players?: Array<{ sprinting?: boolean }>;
}

/**
 * Watches the live match frame and exposes the currently-active campaign
 * matching the most recent trigger event. Auto-clears after 7s.
 */
export function useAdEngine(frame: Frame, elapsed: number) {
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [current, setCurrent] = useState<{ campaign: AdCampaign; event: AdTriggerEvent } | null>(null);
  const lastTriggered = useRef<{ event: AdTriggerEvent; at: number } | null>(null);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    void loadActiveCampaigns().then(setCampaigns);
  }, []);

  // Detect events from elapsed seconds (deterministic schedule).
  useEffect(() => {
    const t = Math.floor(elapsed);
    let event: AdTriggerEvent | null = null;

    if (frame.isGoal) event = "goal";
    else if (frame.inDanger && t % 18 === 0) event = "dangerous_attack";
    else if (frame.players?.some((p) => p.sprinting) && t % 12 === 0) event = "high_speed_run";
    else if (t === 15 || t === 75) event = "corner_kick";
    else if (t === 35 || t === 95) event = "free_kick";
    else if (t === 45) event = "halftime";
    else if (t === 90) event = "fulltime";

    if (!event) return;
    const now = Date.now();
    if (
      lastTriggered.current &&
      lastTriggered.current.event === event &&
      now - lastTriggered.current.at < 8000
    ) {
      return;
    }
    const campaign = pickCampaignForEvent(campaigns, event);
    if (!campaign) return;
    lastTriggered.current = { event, at: now };
    setCurrent({ campaign, event });
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setCurrent(null), 7000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(elapsed), frame.isGoal, frame.inDanger, campaigns.length]);

  const dismiss = () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    setCurrent(null);
  };

  return { current, dismiss, campaigns };
}